import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, Mails, Search, Settings } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Notification from "../../../components/Notification/Notification";
import DateRangePicker from "../../../components/Calender/DateRangePicker";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";
import ViewContactRequestModal from "../../../components/ContactRequests/ViewContactRequestModal";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { can } from "../../../utils/permissions";

/* ===================== STYLES (shared with Help Desk) ===================== */
import styles from "../AdminHelpDesk/AdminHelpDesk.module.scss";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "New", value: "New" },
  { label: "Read", value: "Read" },
  { label: "Responded", value: "Responded" },
  { label: "Archived", value: "Archived" },
];

const statusTone = (status) => {
  switch (status) {
    case "New":
      return "open";
    case "Read":
      return "progress";
    case "Responded":
      return "resolved";
    case "Archived":
      return "closed";
    default:
      return "closed";
  }
};

const AdminContactRequests = () => {
  const navigate = useNavigate();
  const isInternalUser = localStorage.getItem("userType") === "Internal";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* FILTERS */
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  /* MODAL */
  const [viewModal, setViewModal] = useState({ open: false, request: null });

  /* NOTIFY */
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     CONTACT REQUESTS
  ========================= */
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);

      const res = await apiGet(
        "catalog",
        `/contact-requests?${params.toString()}`,
      );

      if (res?.status === "success") {
        const payload = res.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows(list);
        setPagination(
          payload.pagination || {
            page,
            limit,
            total: list.length,
            totalPages: 1,
          },
        );
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load contact requests",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load contact requests"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search, fromDate, toDate]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleResetFilters = () => {
    setStatus("");
    setSearchInput("");
    setSearch("");
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((k) => k + 1);
  };

  /* =========================
     TABLE
  ========================= */
  const tableData = rows.map((row) => ({
    ...row,
    onView: () => setViewModal({ open: true, request: row }),
  }));

  const columns = [
    { label: "Full Name", key: "fullName" },
    { label: "Email", key: "email" },
    { label: "Status", key: "status" },
    { label: "IP Address", key: "ipAddress" },
    { label: "Actions", key: "action" },
  ];

  /* Route is under /admin (internal area), but guard explicitly: superadmin only. */
  if (!isInternalUser) {
    return <Navigate to="/page-not-found" replace />;
  }

  return (
    <div className={styles["admin-helpdesk__page"]}>
      {notification && (
        <div className={styles["admin-helpdesk__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-helpdesk__header"]}>
        <div className={styles["admin-helpdesk__icon-wrapper"]}>
          <Mails size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-helpdesk__title-group"]}>
          <h2 className={styles["admin-helpdesk__title"]}>Contact Requests</h2>
          <p className={styles["admin-helpdesk__subtitle"]}>
            Messages submitted through the public contact-us form.
          </p>
        </div>
        <div className={styles["admin-helpdesk__header-actions"]}>
          {can("contact-requests", "manage-config") && (
            <Button
              text="Config"
              icon={Settings}
              onClick={() => navigate("/admin/contact-requests/config")}
            />
          )}
        </div>
      </div>

      {/* CARD */}
      <div className={styles["admin-helpdesk__card"]}>
        {/* FILTERS */}
        <div className={styles["admin-helpdesk__filter-grid"]}>
          <Input
            type="select"
            placeholder="All Statuses"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          />
          <div className={styles["admin-helpdesk__search-group"]}>
            <Input
              type="search"
              placeholder="Search name, email, message…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>
        </div>

        <div className={styles["admin-helpdesk__action-row"]}>
          <div className={styles["admin-helpdesk__showing-text"]}>
            Showing {rows.length} of {pagination.total}
          </div>
          <div className={styles["admin-helpdesk__filters"]}>
            <DateRangePicker
              key={datePickerKey}
              maxDate={new Date()}
              onChange={(type, value) => {
                if (type === "from") {
                  setFromDate(value);
                  setPage(1);
                }
                if (type === "to") {
                  setToDate(value);
                  setPage(1);
                }
              }}
            />
            {(status || search || fromDate || toDate) && (
              <button
                type="button"
                className={styles["admin-helpdesk__reset-btn"]}
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-helpdesk__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={5} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                if (key === "status") {
                  const tone = statusTone(row.status);
                  return (
                    <span
                      className={`${styles["admin-helpdesk__status-badge"]} ${
                        styles[`admin-helpdesk__status-badge--${tone}`]
                      }`}
                    >
                      {row.status || "--"}
                    </span>
                  );
                }
                if (key === "action") {
                  return (
                    <div className={styles["admin-helpdesk__action-cell"]}>
                      <Eye
                        size={20}
                        className={styles["admin-helpdesk__action-icon"]}
                        onClick={row.onView}
                        aria-label="View Request"
                        role="button"
                        tabIndex={0}
                      />
                    </div>
                  );
                }
                return row[key] || "--";
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages || 1}
          rowsPerPage={limit}
          onPageChange={(next) => setPage(next)}
          onRowsPerPageChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
        />
      </div>

      {/* VIEW MODAL */}
      <ViewContactRequestModal
        open={viewModal.open}
        request={viewModal.request}
        onClose={() => setViewModal({ open: false, request: null })}
        onError={(msg) => showNotification(msg, "error")}
        onSuccess={(msg) => {
          showNotification(msg || "Status updated", "success");
          fetchRequests();
        }}
      />
    </div>
  );
};

export default AdminContactRequests;
